import { useMemo, useRef, useState } from "react";

export type SwipeDirection = "left" | "right" | "up" | "down";

/**
 * Surfaces whose own drag beats any swipe recogniser above them. Radix's slider
 * marks both its root (`data-slot`) and its thumb (`role`); `data-no-swipe` is
 * the escape hatch for anything else that needs to opt out.
 */
const IGNORE_SELECTOR = "[data-slot='slider'],[role='slider'],[data-no-swipe]";

interface SwipeOptions {
  /** Fired once the gesture ends past the distance threshold. */
  onSwipe: (direction: SwipeDirection) => void;
  /** Which axis this surface owns. The other axis is left to the page/scroller. */
  axis: "x" | "y";
  /** Minimum travel, in px, to count as a swipe. */
  threshold?: number;
  /**
   * Live displacement while dragging, for surfaces that follow the finger.
   * Only reports movement along `axis`, and only in `allow` directions.
   */
  track?: boolean;
  allow?: SwipeDirection[];
  /**
   * How far the pointer travels, in px, before the gesture commits to an axis.
   *
   * Small is responsive and right for a control you are already touching. Large
   * is forgiving of a *curved* stroke, which is what a thumb reaching across a
   * phone produces: it pivots from the base of the hand, so the stroke leaves
   * near-vertical and only flattens into the horizontal drag once it is
   * underway. Judged over the first 8px, that reads as a vertical gesture and
   * gets handed to the scroller — so the drawer refuses to close for exactly
   * the reach that needs it most, while a short deliberate drag from the middle
   * of the panel works fine.
   */
  lockDistance?: number;
}

/**
 * A one-axis swipe recogniser.
 *
 * Deliberately pointer-events based rather than touch-events: it then also works
 * for a trackpad drag and a stylus, and it can be cancelled properly. The axis
 * lock matters — a bottom sheet that swallowed horizontal drags would break the
 * photo carousel sitting inside it.
 */
export function useSwipe({
  onSwipe,
  axis,
  threshold = 56,
  track = false,
  allow,
  lockDistance = 8,
}: SwipeOptions): {
  handlers: React.HTMLAttributes<HTMLElement>;
  /** Live offset in px along `axis` while dragging; 0 otherwise. */
  offset: number;
  dragging: boolean;
} {
  const start = useRef<{ x: number; y: number } | null>(null);
  const locked = useRef<"x" | "y" | null>(null);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const handlers = useMemo<React.HTMLAttributes<HTMLElement>>(() => {
    const permitted = (d: SwipeDirection) => !allow || allow.includes(d);

    const reset = () => {
      start.current = null;
      locked.current = null;
      setOffset(0);
      setDragging(false);
    };

    return {
      onPointerDown: (e: React.PointerEvent) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        // A second finger means a pinch, not a swipe. Abandon whatever the first
        // one had started, or zooming a fullscreen photo pages it at the same
        // time — the two fingers rarely travel symmetrically, so the tracked one
        // reads as a perfectly good horizontal drag.
        if (!e.isPrimary) {
          start.current = null;
          return;
        }
        // A drag that starts on a range control belongs to that control. Without
        // this the drawer's swipe-to-close recogniser sees the same pointer
        // stream and dismisses the whole panel the moment you drag a slider
        // left — which is what makes the filters unusable on a phone.
        if (e.target instanceof Element && e.target.closest(IGNORE_SELECTOR)) {
          start.current = null;
          return;
        }
        start.current = { x: e.clientX, y: e.clientY };
        locked.current = null;
        // NOT captured here. See the capture in onPointerMove — taking the
        // pointer on contact breaks every button inside the surface.
      },
      onPointerMove: (e: React.PointerEvent) => {
        const from = start.current;
        if (!from) return;
        const dx = e.clientX - from.x;
        const dy = e.clientY - from.y;

        // Decide the axis once, at `lockDistance`, then stick to it.
        if (!locked.current && Math.hypot(dx, dy) > lockDistance) {
          locked.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
          if (locked.current !== axis) {
            start.current = null;
            return;
          }
          setDragging(true);
          // Captured HERE, not on pointerdown.
          //
          // A captured pointer retargets its compatibility mouse events too, so
          // capturing on contact means the `click` that follows is dispatched at
          // this container instead of at whatever was actually pressed — and
          // every button inside it silently stops working. These surfaces are
          // full of them: the gallery's arrows and fullscreen control, and the
          // whole filter drawer.
          //
          // By this point the pointer has travelled past `lockDistance` along
          // our axis, so it is a drag and not a tap, and suppressing the click
          // is the correct outcome rather than a side effect. A tap never gets
          // here, so its button still receives it.
          e.currentTarget.setPointerCapture?.(e.pointerId);
        }
        if (locked.current !== axis) return;

        const delta = axis === "x" ? dx : dy;
        const direction: SwipeDirection =
          axis === "x" ? (delta < 0 ? "left" : "right") : delta < 0 ? "up" : "down";
        if (track) setOffset(permitted(direction) ? delta : delta * 0.15);
      },
      onPointerUp: (e: React.PointerEvent) => {
        const from = start.current;
        if (!from) {
          reset();
          return;
        }
        const delta = axis === "x" ? e.clientX - from.x : e.clientY - from.y;
        const direction: SwipeDirection =
          axis === "x" ? (delta < 0 ? "left" : "right") : delta < 0 ? "up" : "down";
        reset();
        if (Math.abs(delta) >= threshold && permitted(direction)) onSwipe(direction);
      },
      onPointerCancel: reset,
    };
  }, [axis, threshold, track, allow, onSwipe, lockDistance]);

  return { handlers, offset, dragging };
}
